import { inject, Injectable } from '@angular/core';
import { deleteObject, getDownloadURL, ref, Storage, uploadBytes } from '@angular/fire/storage';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../core/auth/services/auth.service';
import { ToastService } from '../../core/toast/toast.service';
import { AudioTranscodeService } from './audio-transcode.service';

/** Max file sizes in bytes — must match Firebase Storage rules. */
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;  // 10 MB
const MAX_AUDIO_SIZE = 50 * 1024 * 1024;  // 50 MB

/** Result of a successful file upload to Firebase Storage. */
export interface UploadResult {
  /** Download URL for display or linking. */
  url: string;
  /** Storage path for reliable deletion (required for deleteFile). */
  path: string;
}

/**
 * Reusable service for uploading and deleting files in Firebase Storage.
 * Used across onboarding, timeline, profiles, and other features.
 */
@Injectable({ providedIn: 'root' })
export class StorageUploadService {
  private readonly storage = inject(Storage);
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly audioTranscode = inject(AudioTranscodeService);

  /**
   * Uploads a file or blob to Firebase Storage under the given path suffix.
   * Builds full path as users/{uid}/{pathSuffix}/{timestamp}-{random}.{ext}
   */
  async uploadFile(file: File | Blob, pathSuffix: string, maxSizeOverride?: number): Promise<UploadResult> {
    const uid = this.authService.user()?.uid;
    if (!uid) {
      throw new Error('User must be authenticated to upload files');
    }

    // Client-side size validation before upload
    const isAudio = file.type.startsWith('audio/') || pathSuffix.includes('audio');
    const maxSize = maxSizeOverride ?? (isAudio ? MAX_AUDIO_SIZE : MAX_IMAGE_SIZE);
    if (file.size > maxSize) {
      const maxMB = Math.round(maxSize / (1024 * 1024));
      this.toast.error(this.translate.instant('UPLOAD.ERROR.FILE_TOO_LARGE', { max: maxMB }));
      throw new Error(`File exceeds ${maxMB}MB limit`);
    }

    // Transcode audio to MP4/AAC if needed (ensures universal playback)
    let uploadBlob: File | Blob = file;
    if (isAudio && this.audioTranscode.needsTranscode(file)) {
      uploadBlob = await this.audioTranscode.ensureMp4(file);
    }

    const ext = this.getExtension(uploadBlob);
    const filename = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const fullPath = `users/${uid}/${pathSuffix}/${filename}`;
    const storageRef = ref(this.storage, fullPath);

    try {
      await uploadBytes(storageRef, uploadBlob);
      const url = await getDownloadURL(storageRef);
      return { url, path: fullPath };
    } catch (e) {
      console.error('Storage upload failed', e);
      const code =
        typeof e === 'object' && e !== null && 'code' in e
          ? String((e as { code: unknown }).code)
          : '';
      const key =
        code === 'storage/retry-limit-exceeded'
          ? 'UPLOAD.ERROR.NETWORK_TIMEOUT'
          : 'UPLOAD.ERROR.FAILED';
      this.toast.error(this.translate.instant(key));
      throw e;
    }
  }

  /**
   * Deletes a file from Storage by its path.
   * For legacy data with only download URL, use refFromURL (not implemented here).
   */
  async deleteFile(path: string): Promise<void> {
    try {
      const storageRef = ref(this.storage, path);
      await deleteObject(storageRef);
    } catch (e) {
      console.warn('Could not delete file from Storage', path, e);
      // Do not throw — caller may want to continue (e.g. remove Firestore doc anyway)
    }
  }

  private getExtension(file: File | Blob): string {
    if (file instanceof File) {
      const ext = file.name.split('.').pop()?.toLowerCase();
      return ext ?? 'jpg';
    }
    // Blob — detect from MIME type
    if (file.type.includes('mp4') || file.type.includes('mpeg')) return 'mp4';
    if (file.type.includes('webm')) return 'webm';
    if (file.type.includes('ogg')) return 'ogg';
    return 'mp4';
  }
}
